"use client";
import React, { useEffect, useState } from "react";
import { Button, Tag, Spin, Empty } from "antd";
import { Link } from "@/navigation";
import { getEbookList } from "@/lib/api/resource";
import { PlusCircleOutlined } from "@ant-design/icons";
import { useTranslations } from "next-intl";
import { MediaType } from "@/lib/@types/api/media";

const EbooksLibrary = ({
	locale,
	categoryList,
}: {
	locale: string;
	categoryList: MediaType.EbookCategory[];
}) => {
	const t = useTranslations();
	const [ebookList, setEbookList] = useState<any>([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const [loadMoreLoading, setLoadMoreLoading] = useState(false);
	const [pageSize, setPageSize] = useState(1);
	const [total, setTotal] = useState(0);
	const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
	const limit = 10;

	useEffect(() => {
		setPageSize(1);
		fetchEbookList(1, true);
	}, [selectedCategoryId]);

	const fetchEbookList = async (page: number = 1, isReset: boolean = false) => {
		if (isReset) {
			setSearchLoading(true);
		} else {
			setLoadMoreLoading(true);
		}

		await getEbookList({
			lang_code: { lang_code: locale },
			page,
			limit,
			...(selectedCategoryId ? { category_id_list: [selectedCategoryId] } : {}),
		})
			.then((res) => {
				if (res.detail) {
					const { ebook_list, count } = res.detail;
					setTotal(count);
					if (isReset) {
						setEbookList(ebook_list);
					} else {
						setEbookList((prev: any) => [...prev, ...ebook_list]);
					}
					setSearchLoading(false);
					setLoadMoreLoading(false);
				}
			})
			.catch((error) => {
				setSearchLoading(false);
				setLoadMoreLoading(false);
				console.error("Error fetching ebook list:", error);
			});
	};

	const handleLoadMore = () => {
		const nextPage = pageSize + 1;
		setPageSize(nextPage);
		fetchEbookList(nextPage, false);
	};

	const handleDownload = (e, item) => {
		e.preventDefault();
		window.open(`/ebook/${item.e_book_name}?id=${item.e_book_id}`, "_self");
	};

	return (
		<div className="container py-16 max-md:py-8">
			<div className="flex flex-col gap-[12px]">
				<h1 className="text-[3rem] font-bold leading-normal text-[#212121] max-md:text-center max-md:text-2xl">
					{t("resource.eBooks Library")}
				</h1>
				<div className="flex flex-row items-center justify-between gap-[12px] max-md:flex-col">
					<div className="text-center text-[18px] text-[#545454] max-md:text-base">
						{t("resource.eBooks Library Desc")}
					</div>
					<div className="flex justify-end max-md:w-full">
						<Link
							className="flex cursor-pointer justify-end gap-[20px] text-[#212121] hover:text-[#0e6aed] max-md:gap-[8px]"
							href="/blog"
						>
							<span className="whitespace-nowrap text-[20px] max-md:text-[14px]">
								{t("resource.View More")}
							</span>
							<PlusCircleOutlined className="text-[26px] max-md:text-[18px]" />
						</Link>
					</div>
				</div>
			</div>
			<div>
				<div className="flex flex-wrap items-center gap-3">
					<Tag
						className={`!my-2 cursor-pointer !rounded-full !border-0 !px-5 !py-2 !text-[16px] transition-all ${
							selectedCategoryId === null
								? "!bg-[#65a525] !text-white"
								: "!bg-[#f0f0f0] !text-[#666] hover:!bg-[#e0e0e0]"
						}`}
						onClick={() => setSelectedCategoryId(null)}
					>
						{t("resource.All")}
					</Tag>
					{categoryList?.map((item: MediaType.EbookCategory, index: number) => {
						const isSelected = selectedCategoryId === item.e_book_category_id;
						return (
							<Tag
								className={`!my-2 cursor-pointer !rounded-full !border-0 !px-5 !py-2 !text-[16px] transition-all ${
									isSelected ? "!bg-[#65a525] !text-white" : "!bg-[#f0f0f0] !text-[#666] hover:!bg-[#e0e0e0]"
								}`}
								key={item.e_book_category_id || index}
								onClick={() => setSelectedCategoryId(item.e_book_category_id)}
							>
								{item.e_book_category_name}
							</Tag>
						);
					})}
				</div>
				<div className="mt-4 flex min-h-[50vh] flex-col gap-[36px]">
					{searchLoading ? (
						<Spin size="large" />
					) : (
						<>
							{ebookList?.length > 0 ? (
								<>
									<div className="grid grid-cols-2 gap-[36px] max-md:grid-cols-1">
										{ebookList?.slice(0, 2)?.map((item: any, index: number) => {
											return (
												<Link
													href={`/ebook/${item.e_book_id}`}
													className="library-big-item relative flex h-[355px] transform flex-col items-start justify-end overflow-hidden rounded-[6px] bg-cover p-8 transition-all duration-700 hover:-translate-y-2 max-md:h-[255px]"
													style={{ backgroundImage: `url(${item.cover_image_url || ""})` }}
													key={index}
												>
													<div className="absolute bottom-0 left-0 right-0 top-0 bg-[rgba(0,0,0,.3)]"></div>
													<div className="relative">
														<button className="flex w-[fit-content] items-center gap-[4px] py-1 text-[16px] text-white">
															<img className="h-[15px] w-[17px]" src={"/image/resource/download.png"} />
															<span>{t("resource.Download")}</span>
														</button>
														<div className="library-big-item-text line-clamp-2 text-[25px] font-bold leading-[30px] text-white">
															{item.e_book_name}
														</div>
														<div className="text-base text-white">{`${t("resource.By")} ${
															item.e_book_author
														}`}</div>
													</div>
												</Link>
											);
										})}
									</div>
									<div className="grid grid-cols-4 gap-[36px] max-md:grid-cols-1">
										{ebookList?.slice(2)?.map((item: any, index: number) => {
											return (
												<Link className="library-small-item" href={`/ebook/${item.e_book_id}`} key={index}>
													<div
														className="relative mb-3 flex h-[255px] flex-col items-start justify-start overflow-hidden rounded-[6px] bg-cover px-4 py-2 max-md:h-[255px]"
														style={{ backgroundImage: `url(${item.cover_image_url || ""})` }}
													>
														<div className="absolute bottom-0 left-0 right-0 top-0 bg-[rgba(0,0,0,.3)]"></div>
														<div className="relative">
															<button className="flex w-[fit-content] items-center gap-[4px] py-1 text-[16px] text-white">
																<img className="h-[15px] w-[17px]" src={"/image/resource/download.png"} />
																<span>{t("resource.Download")}</span>
															</button>
														</div>
													</div>
													<div className="flex flex-col gap-3">
														<div className="library-small-item-text line-clamp-2 text-[18px] font-bold leading-normal text-black md:h-[58px]">
															{item.e_book_name}
														</div>
														<div className="text-[12px] text-black">{`${t("resource.By")} ${
															item.e_book_author
														}`}</div>
													</div>
												</Link>
											);
										})}
									</div>
									{/* 加载更多按钮 */}
									{ebookList.length < total && (
										<div className="flex justify-center pt-8">
											<Button
												type="primary"
												size="large"
												loading={loadMoreLoading}
												onClick={handleLoadMore}
												className="!h-[48px] !min-w-[200px] !rounded-[6px] !bg-[#65a525] !text-[16px] !font-medium hover:!bg-[#50821a]"
												icon={!loadMoreLoading && <PlusCircleOutlined />}
											>
												{loadMoreLoading ? t("resource.Loading") : t("resource.Load More")}
											</Button>
										</div>
									)}
								</>
							) : (
								<Empty />
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default EbooksLibrary;
